import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DsModal from '../../../common/DsModal';
import ChatImageModal from '../composer/ChatImageModal';
import { buildChatAssetUrl, buildChatAssetUrlWithVariant } from '../composer/chatAssetUtils';
import { getFileVisual } from '../composer/fileIconClass';
import '../../../../css/common/chat/ChatAssetModal.css';

const PAGE = 40;
const API_URL = process.env.REACT_APP_API_URL;
const MESSAGE_IMAGE_PATH = process.env.REACT_APP_MESSAGE_IMAGE_PATH;
const MESSAGE_FILE_PATH = process.env.REACT_APP_MESSAGE_FILE_PATH;

// チャット本文と同じ proxy 経由URL（署名URLの内部ホスト minio:9000 はブラウザ非到達）。
const imageProxyUrl = (storedName) =>
  buildChatAssetUrlWithVariant({ apiUrl: API_URL, root: MESSAGE_IMAGE_PATH, file: storedName, variant: 'proxy' });
const fileDownloadUrl = (storedName) =>
  buildChatAssetUrl({ apiUrl: API_URL, root: MESSAGE_FILE_PATH, file: storedName });

const keyOf = (item) => `${item.kind}:${item.assetId}`;

const fileVisual = (fileType, fileName) => {
  const visual = getFileVisual(fileName, fileType);
  return { icon: visual.iconClass, cls: visual.kind === 'powerpoint' ? 'is-ppt' : `is-${visual.kind}` };
};

const formatBytes = (n) => {
  const bytes = Number(n);
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
};

const formatDateTime = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

// loadMedia(offset, limit) => { items, hasMore } と downloadZip({ imageIds, fileIds }) を注入。
export default function ChatMediaModal({ isOpen, onClose, loadMedia, downloadZip }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState('');
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [downloading, setDownloading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const offsetRef = useRef(0);
  const loadedRef = useRef(false);

  const load = useCallback(async (reset) => {
    if (typeof loadMedia !== 'function') return;
    setLoading(true);
    setError('');
    try {
      const off = reset ? 0 : offsetRef.current;
      const data = await loadMedia(off, PAGE);
      const next = Array.isArray(data?.items) ? data.items : [];
      setItems((prev) => (reset ? next : [...prev, ...next]));
      offsetRef.current = off + PAGE;
      setHasMore(Boolean(data?.hasMore));
    } catch {
      setError('写真・ファイルの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, [loadMedia]);

  useEffect(() => {
    if (isOpen && !loadedRef.current) {
      loadedRef.current = true;
      offsetRef.current = 0;
      setItems([]);
      setSelectMode(false);
      setSelected(new Set());
      load(true);
    }
    if (!isOpen) loadedRef.current = false;
  }, [isOpen, load]);

  const toggle = useCallback((item) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const k = keyOf(item);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }, []);

  const selectedList = useMemo(() => {
    const imageIds = [];
    const fileIds = [];
    for (const item of items) {
      if (!selected.has(keyOf(item))) continue;
      if (item.kind === 'image') imageIds.push(item.assetId);
      else fileIds.push(item.assetId);
    }
    return { imageIds, fileIds };
  }, [items, selected]);

  const handleTileClick = useCallback((item) => {
    if (selectMode) {
      toggle(item);
      return;
    }
    // チャット本文と同じ挙動：画像は拡大表示、ファイルはダウンロード。
    const storedName = item.storedName || item.fileName;
    if (item.kind === 'image' && storedName) {
      setPreviewImage({ url: imageProxyUrl(storedName), name: item.fileName });
      return;
    }
    if (item.kind === 'file' && storedName) {
      const url = fileDownloadUrl(storedName);
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, [selectMode, toggle]);

  const handleBulkDownload = useCallback(async () => {
    const { imageIds, fileIds } = selectedList;
    if ((!imageIds.length && !fileIds.length) || typeof downloadZip !== 'function') return;
    setDownloading(true);
    setError('');
    try {
      await downloadZip({ imageIds, fileIds });
    } catch (e) {
      setError(e?.message || 'ダウンロードに失敗しました。');
    } finally {
      setDownloading(false);
    }
  }, [downloadZip, selectedList]);

  if (!isOpen) return null;

  const selectedCount = selectedList.imageIds.length + selectedList.fileIds.length;

  return (
    <DsModal open={isOpen} onClose={onClose} title="写真・ファイル" size="lg" className="chat-asset-modal chat-media-modal">
      <div className="chat-asset-modal__note">
        このチャットで送られた写真・ファイルです（直近1年分）。写真はタップで拡大、ファイルはタップで保存できます。
      </div>

      <div className="chat-media-modal__toolbar">
        <button
          type="button"
          className={`chat-media-modal__tool-btn${selectMode ? ' is-active' : ''}`}
          onClick={() => {
            setSelectMode((v) => !v);
            setSelected(new Set());
          }}
        >
          <i className={`fa-solid ${selectMode ? 'fa-xmark' : 'fa-square-check'}`} aria-hidden="true" />
          {' '}{selectMode ? '選択をやめる' : '選択'}
        </button>
        {selectMode ? (
          <>
            <button type="button" className="chat-media-modal__tool-btn" onClick={() => setSelected(new Set(items.map(keyOf)))}>
              全選択
            </button>
            <button type="button" className="chat-media-modal__tool-btn" onClick={() => setSelected(new Set())}>
              解除
            </button>
          </>
        ) : null}
      </div>

      {error ? <div className="chat-asset-modal__error">{error}</div> : null}
      {!loading && items.length === 0 && !error ? (
        <div className="chat-asset-modal__empty">写真・ファイルはまだありません。</div>
      ) : null}

      <div className="chat-media-modal__grid">
        {items.map((item) => {
          const isSelected = selected.has(keyOf(item));
          const isFile = item.kind === 'file';
          const visual = isFile ? fileVisual(item.fileType, item.fileName) : null;
          const sizeLabel = isFile ? formatBytes(item.fileSize) : '';
          const dateLabel = formatDateTime(item.createdAt);
          return (
            <button
              type="button"
              key={keyOf(item)}
              className={`chat-media-tile${isSelected ? ' is-selected' : ''}`}
              onClick={() => handleTileClick(item)}
              title={item.fileName}
            >
              <span className={`chat-media-tile__box${isFile ? ` chat-media-tile__box--file ${visual.cls}` : ''}`}>
                {item.kind === 'image' ? (
                  (item.storedName || item.fileName) ? (
                    <img className="chat-media-tile__img" src={imageProxyUrl(item.storedName || item.fileName)} alt={item.fileName || ''} loading="lazy" />
                  ) : (
                    <i className="fa-solid fa-image chat-media-tile__glyph" aria-hidden="true" />
                  )
                ) : (
                  <i className={`fa-solid ${visual.icon} chat-media-tile__glyph`} aria-hidden="true" />
                )}
                {selectMode ? (
                  <span className="chat-media-tile__check">
                    {isSelected ? <i className="fa-solid fa-check" aria-hidden="true" /> : null}
                  </span>
                ) : null}
              </span>
              <span className="chat-media-tile__meta">
                {isFile ? <span className="chat-media-tile__name">{item.fileName}</span> : null}
                {item.senderName ? <span className="chat-media-tile__sender">{item.senderName}</span> : null}
                <span className="chat-media-tile__sub">
                  {sizeLabel ? <span className="chat-media-tile__size">{sizeLabel}</span> : null}
                  {dateLabel ? <span className="chat-media-tile__date">{dateLabel}</span> : null}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {loading ? <div className="chat-asset-modal__loading">読み込み中…</div> : null}
      {hasMore && !loading ? (
        <button type="button" className="chat-asset-modal__more" onClick={() => load(false)}>
          もっと見る
        </button>
      ) : null}

      {selectMode ? (
        <div className="chat-media-modal__actionbar">
          <span className="chat-media-modal__actionbar-count">{selectedCount}件を選択中</span>
          <span className="chat-media-modal__actionbar-spacer" />
          <button
            type="button"
            className="chat-media-modal__download-btn"
            disabled={selectedCount === 0 || downloading}
            onClick={handleBulkDownload}
          >
            <i className="fa-solid fa-download" aria-hidden="true" />
            {' '}{downloading ? '準備中…' : 'まとめてダウンロード'}
          </button>
        </div>
      ) : null}

      <ChatImageModal
        isOpen={Boolean(previewImage)}
        src={previewImage?.url || ''}
        alt={previewImage?.name || '写真'}
        showNav={false}
        downloadItems={previewImage ? [{ downloadUrl: previewImage.url, name: previewImage.name }] : []}
        currentIndex={0}
        onClose={() => setPreviewImage(null)}
      />
    </DsModal>
  );
}
